"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import { Fragment, useEffect, useState, type ComponentProps, type ReactNode } from "react";
import { useIsApple } from "@/components/wingo/hooks/use-media-query";
import { formatShortcut, formatShortcutText, matchesShortcut, parseShortcut, type ParsedShortcut } from "@/components/wingo/hooks/use-shortcut";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type KbdSize = "sm" | "md" | "lg";
export type KbdVariant = "soft" | "outline" | "solid" | "ghost";
export type KbdRadius = "none" | "sm" | "md" | "lg" | "full";
export type KbdPlatform = "auto" | "apple" | "other";
export type KbdSlot = "key" | "separator";
export type { Tone as KbdTone };

// cap height 16 / 20 / 24px, never narrower than it is tall
const SIZES: Record<KbdSize, string> = {
  sm: "h-4 min-w-4 px-[3px] text-[10px] [&_svg:not([class*='size-'])]:size-2.5",
  md: "h-5 min-w-5 px-1 text-[11px] [&_svg:not([class*='size-'])]:size-3",
  lg: "h-6 min-w-6 px-1.5 text-[12px] [&_svg:not([class*='size-'])]:size-3.5",
};

const GAPS: Record<KbdSize, string> = { sm: "gap-0.5", md: "gap-1", lg: "gap-1" };
const SEPARATOR_SIZES: Record<KbdSize, string> = { sm: "text-[10px]", md: "text-[11px]", lg: "text-[12px]" };

// md is the DESIGN look (5px); every step scales with the cap so all sizes read as the same shape
const RADII: Record<KbdRadius, Record<KbdSize, string>> = {
  none: { sm: "rounded-none", md: "rounded-none", lg: "rounded-none" },
  sm: { sm: "rounded-[3px]", md: "rounded-[4px]", lg: "rounded-[5px]" },
  md: { sm: "rounded-[4px]", md: "rounded-[5px]", lg: "rounded-[6px]" },
  lg: { sm: "rounded-[5px]", md: "rounded-[7px]", lg: "rounded-[8px]" },
  full: { sm: "rounded-full", md: "rounded-full", lg: "rounded-full" },
};

// neutral keeps the quiet gray key from DESIGN 14: a 5% tint with a 1px bottom edge that reads as a keycap. the dark
// solid fill yields to data-pressed (dark: sorts after data-*, so it would otherwise keep a held key gray)
const NEUTRAL: Record<KbdVariant, string> = {
  soft: "bg-foreground/5 text-muted-foreground shadow-[inset_0_-1px_0_0_color-mix(in_oklab,rgb(var(--wui-foreground))_15%,transparent)]",
  outline:
    "border border-foreground/15 text-muted-foreground shadow-[inset_0_-1px_0_0_color-mix(in_oklab,rgb(var(--wui-foreground))_15%,transparent)]",
  solid:
    "bg-popover text-popover-foreground shadow-[inset_0_-1px_0_0_color-mix(in_oklab,rgb(var(--wui-foreground))_12%,transparent),var(--wui-elevation-raised)] dark:[&:not([data-pressed])]:bg-[color-mix(in_oklab,rgb(var(--wui-foreground))_10%,rgb(var(--wui-popover)))]",
  ghost: "",
};

// any other tone or a custom color paints through the --tone-* vars (lib/tones)
const TONED: Record<KbdVariant, string> = {
  soft: "bg-[color:var(--tone-soft)] text-[color:var(--tone-soft-foreground)] shadow-[inset_0_-1px_0_0_var(--tone-border)]",
  outline: "border border-[color:var(--tone-border)] text-[color:var(--tone-soft-foreground)] shadow-[inset_0_-1px_0_0_var(--tone-border)]",
  solid: "bg-[color:var(--tone)] text-[color:var(--tone-foreground)] shadow-[inset_0_-1px_0_0_rgb(0_0_0/0.18),var(--wui-elevation-raised)]",
  ghost: "",
};

// the root carries the hint color; ghost caps and separators inherit it, so a color class on the root (text-white/60
// inside a tooltip) reaches every part
const ROOT_TEXT = { neutral: "text-muted-foreground", toned: "text-[color:var(--tone-soft-foreground)]" };

// a held key (listen) sinks 1px and lights up in the tone's fill; the edge line goes, like a key at the bottom of its travel
const PRESSED: Record<KbdVariant, string> = {
  soft: "data-[pressed]:translate-y-px data-[pressed]:bg-[color:var(--tone)] data-[pressed]:text-[color:var(--tone-foreground)] data-[pressed]:shadow-none",
  outline:
    "data-[pressed]:translate-y-px data-[pressed]:border-transparent data-[pressed]:bg-[color:var(--tone)] data-[pressed]:text-[color:var(--tone-foreground)] data-[pressed]:shadow-none",
  solid: "data-[pressed]:translate-y-px data-[pressed]:bg-[color:var(--tone)] data-[pressed]:text-[color:var(--tone-foreground)] data-[pressed]:shadow-none",
  ghost: "data-[pressed]:bg-[color-mix(in_oklab,currentColor_15%,transparent)]",
};

const CAP =
  "inline-flex shrink-0 items-center justify-center gap-0.5 whitespace-nowrap font-sans font-medium leading-none tabular-nums transition-[translate,background-color,color,border-color] duration-fast ease-smooth motion-reduce:data-[pressed]:translate-y-0";

// ghost is plain text (menus, tooltips): no box, so no padding or minimum width to hold it open
const GHOST_CAP = "min-w-0 px-0.5";

// ---------- labels ----------

const SPOKEN_KEYS: Record<string, string> = {
  " ": "Space",
  escape: "Escape",
  enter: "Enter",
  backspace: "Backspace",
  delete: "Delete",
  tab: "Tab",
  arrowup: "Up arrow",
  arrowdown: "Down arrow",
  arrowleft: "Left arrow",
  arrowright: "Right arrow",
  pageup: "Page up",
  pagedown: "Page down",
  capslock: "Caps lock",
  home: "Home",
  end: "End",
  "/": "Slash",
  "?": "Question mark",
  ".": "Period",
  ",": "Comma",
  "+": "Plus",
  "-": "Minus",
  "=": "Equals",
  ";": "Semicolon",
  "'": "Quote",
  "`": "Backtick",
  "\\": "Backslash",
  "[": "Left bracket",
  "]": "Right bracket",
  "|": "Vertical bar",
  "*": "Asterisk",
  "&": "Ampersand",
  "_": "Underscore",
  "~": "Tilde",
  "!": "Exclamation mark",
  "#": "Hash",
  "@": "At",
};

// abbreviations written on caps, spelled out so a screen reader does not read "Ctrl" letter by letter
const SPOKEN_WORDS: Record<string, string> = {
  ctrl: "Control",
  cmd: "Command",
  opt: "Option",
  win: "Windows",
  esc: "Escape",
  del: "Delete",
  ins: "Insert",
  pgup: "Page up",
  pgdn: "Page down",
};

// glyphs a screen reader would otherwise read as "place of interest sign" or skip, and the key each one stands for
const GLYPHS: Record<string, { word: string; id: string }> = {
  "⌘": { word: "Command", id: "meta" },
  "⇧": { word: "Shift", id: "shift" },
  "⌥": { word: "Option", id: "alt" },
  "⌃": { word: "Control", id: "ctrl" },
  "↑": { word: "Up arrow", id: "key:arrowup" },
  "↓": { word: "Down arrow", id: "key:arrowdown" },
  "←": { word: "Left arrow", id: "key:arrowleft" },
  "→": { word: "Right arrow", id: "key:arrowright" },
  "↩": { word: "Return", id: "key:enter" },
  "↵": { word: "Enter", id: "key:enter" },
  "⌫": { word: "Delete", id: "key:backspace" },
  "⌦": { word: "Forward delete", id: "key:delete" },
  "⎋": { word: "Escape", id: "key:escape" },
  "⇥": { word: "Tab", id: "key:tab" },
  "⇪": { word: "Caps lock", id: "key:capslock" },
  "␣": { word: "Space", id: "key: " },
};
const GLYPH_RE = new RegExp(`[${Object.keys(GLYPHS).join("")}]`, "g");

const MODIFIER_WORDS: Record<string, string> = {
  ctrl: "ctrl",
  control: "ctrl",
  alt: "alt",
  option: "alt",
  opt: "alt",
  shift: "shift",
  cmd: "meta",
  command: "meta",
  meta: "meta",
  win: "meta",
};

function spokenKey(key: string) {
  const named = SPOKEN_KEYS[key];
  if (named) return named;
  if (key.length === 1 || /^f\d{1,2}$/.test(key)) return key.toUpperCase();
  return key[0].toUpperCase() + key.slice(1);
}

// "Shift Command P" on apple devices, "Control Shift P" elsewhere; the caps themselves stay aria-hidden
function spokenShortcut(parsed: ParsedShortcut, apple: boolean) {
  const words: string[] = [];
  if (parsed.ctrl || (parsed.mod && !apple)) words.push("Control");
  if (parsed.alt) words.push(apple ? "Option" : "Alt");
  if (parsed.shift) words.push("Shift");
  if (parsed.meta || (parsed.mod && apple)) words.push(apple ? "Command" : "Windows");
  words.push(spokenKey(parsed.key));
  return words.join(" ");
}

// "⌘K" -> "Command K", "Ctrl+K" -> "Control K", "Esc" -> "Escape"; other labels come back unchanged
function spokenLabel(label: string) {
  return label
    .replace(GLYPH_RE, (glyph) => ` ${GLYPHS[glyph].word} `)
    .split(/\s+|(?<=\S)\+(?=\S)/)
    .filter(Boolean)
    .map((word) => SPOKEN_WORDS[word.toLowerCase()] ?? word)
    .join(" ");
}

// the ids of a parsed combo, in the order formatShortcut prints its caps (control, option, shift, command, key)
function comboIds(parsed: ParsedShortcut, apple: boolean) {
  const ids: string[] = [];
  if (parsed.ctrl || (parsed.mod && !apple)) ids.push("ctrl");
  if (parsed.alt) ids.push("alt");
  if (parsed.shift) ids.push("shift");
  if (parsed.meta || (parsed.mod && apple)) ids.push("meta");
  ids.push(`key:${parsed.key}`);
  return ids;
}

// which physical keys a written label stands for, so listen can press it: "ctrl" | "alt" | "shift" | "meta" |
// "key:<key>". handles glyphs ("⌘", "⇧⌘P"), modifier words ("Ctrl") and combos ("Ctrl+K"); [] when unknown
function keyIdsOf(label: string, apple: boolean): string[] {
  const ids: string[] = [];
  const rest = label.replace(GLYPH_RE, (glyph) => {
    ids.push(GLYPHS[glyph].id);
    return "";
  });
  const text = rest.trim();
  if (!text) return ids;
  const lower = text.toLowerCase();
  if (lower === "mod") return [...ids, apple ? "meta" : "ctrl"];
  if (MODIFIER_WORDS[lower]) return [...ids, MODIFIER_WORDS[lower]];
  const parsed = parseShortcut(text);
  return parsed ? [...ids, ...comboIds(parsed, apple)] : [];
}

const MODIFIER_KEYS: Record<string, string> = { Control: "ctrl", Alt: "alt", Shift: "shift", Meta: "meta" };

const NO_MODIFIERS = { ctrlKey: false, metaKey: false, altKey: false, shiftKey: false };

// the key alone, whatever modifiers are down (option+k on a mac types "˚", so this falls back to event.code)
function isKey(event: KeyboardEvent, key: string) {
  return matchesShortcut(
    { key: event.key, code: event.code, ...NO_MODIFIERS },
    { key, mod: false, ctrl: false, meta: false, alt: false, shift: false },
    false,
  );
}

// ids travel as one newline-joined string: "|", "&" and "+" are keys of their own, and a string state means an
// unrelated keystroke never re-renders
const JOIN = "\n";

// the subset of ids held down right now
function useHeldKeys(ids: string[], enabled: boolean) {
  const [held, setHeld] = useState("");
  const signature = Array.from(new Set(ids)).join(JOIN);

  useEffect(() => {
    if (!enabled || !signature) return;
    const wanted = signature.split(JOIN);
    const keys = wanted.filter((id) => id.startsWith("key:")).map((id) => id.slice(4));
    const down = new Set<string>();

    const sync = (event: KeyboardEvent, released?: string) => {
      const now = new Set<string>();
      if (event.ctrlKey) now.add("ctrl");
      if (event.altKey) now.add("alt");
      if (event.shiftKey) now.add("shift");
      if (event.metaKey) now.add("meta");
      // some sources (automation, remote desktops) still flag a modifier on its own keyup
      if (released) now.delete(released);
      down.forEach((key) => now.add(`key:${key}`));
      setHeld(wanted.filter((id) => now.has(id)).join(JOIN));
    };
    const onDown = (event: KeyboardEvent) => {
      for (const key of keys) if (isKey(event, key)) down.add(key);
      sync(event);
    };
    const onUp = (event: KeyboardEvent) => {
      // macos swallows the keyup of every key pressed while ⌘ was down, so letting go of ⌘ releases them all
      if (event.key === "Meta") down.clear();
      else for (const key of keys) if (isKey(event, key)) down.delete(key);
      sync(event, MODIFIER_KEYS[event.key]);
    };
    // a key held while the window loses focus never sends its keyup
    const reset = () => {
      down.clear();
      setHeld("");
    };

    window.addEventListener("keydown", onDown, true);
    window.addEventListener("keyup", onUp, true);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("keydown", onDown, true);
      window.removeEventListener("keyup", onUp, true);
      window.removeEventListener("blur", reset);
    };
  }, [signature, enabled]);

  return enabled && held ? new Set(held.split(JOIN)) : null;
}

// parts: a combined apple cap keeps its glyphs apart (⇧ ⌘ P with a hair of space), like the menus in macos.
// ids: the physical keys the cap stands for (a combined cap has several); empty when unknown
type Cap = { label: string; parts?: string[]; ids: string[] };

export type KbdProps = Omit<ComponentProps<"kbd">, "color"> & {
  // a combo in mod+k notation ("mod+k", "shift+mod+p", "alt+n", "/"), shown as ⌘K on apple devices and Ctrl+K elsewhere
  shortcut?: string;
  // one cap per entry, shown as written: a sequence (["G", "I"]) or a hint that is not one combo (["↑", "↓"])
  keys?: string[];
  // the whole combo in one cap (⌘K, Ctrl+K) instead of one cap per key
  combined?: boolean;
  // force a notation; auto shows the non-apple form on the server and during hydration, then the device's
  platform?: KbdPlatform;
  // sm 16px, md 20px, lg 24px caps
  size?: KbdSize;
  // soft: the quiet gray key, outline: a bordered key, solid: a raised physical key, ghost: text only (menus, tooltips)
  variant?: KbdVariant;
  tone?: Tone;
  // any css color; replaces the tone
  color?: string;
  radius?: KbdRadius;
  // between caps when not combined, e.g. "+" or "then"
  separator?: ReactNode;
  // hidden on touch screens, where a keyboard hint means nothing
  hideOnTouch?: boolean;
  // caps press down while the matching keys are held on a physical keyboard
  listen?: boolean;
  // what screen readers hear instead of the caps; defaults to the combo spelled out in english ("Shift Command P"),
  // so pass a translation ("Maj Commande P") on localized pages
  srLabel?: string;
  classNames?: Partial<Record<KbdSlot, string>>;
};

function CapLabel({ cap, hidden }: { cap: Cap; hidden?: boolean }) {
  if (!cap.parts) return hidden ? <span aria-hidden>{cap.label}</span> : <>{cap.label}</>;
  return (
    <span aria-hidden={hidden || undefined} className="inline-flex items-center gap-[0.08em]">
      {cap.parts.map((part, i) => (
        <span key={i}>{part}</span>
      ))}
    </span>
  );
}

// what to draw: caps with the key each stands for, the spoken form, or a plain node. shortcut wins, then keys, then
// children; an unparseable shortcut falls back
function resolve(
  shortcut: string | undefined,
  keys: string[] | undefined,
  children: ReactNode,
  combined: boolean,
  separator: ReactNode,
  apple: boolean,
): { caps: Cap[]; spoken: string; node: ReactNode } {
  const parsed = shortcut ? parseShortcut(shortcut) : null;
  if (shortcut && parsed) {
    const ids = comboIds(parsed, apple);
    const spoken = spokenShortcut(parsed, apple);
    if (combined) {
      const parts = apple ? formatShortcut(shortcut, apple) : undefined;
      return { caps: [{ label: formatShortcutText(shortcut, apple), parts, ids }], spoken, node: null };
    }
    return { caps: formatShortcut(shortcut, apple).map((label, i) => ({ label, ids: ids[i] ? [ids[i]] : [] })), spoken, node: null };
  }

  const list = keys?.map((k) => k.trim()).filter(Boolean) ?? [];
  if (list.length) {
    const ids = list.map((k) => keyIdsOf(k, apple));
    // a word separator ("then", "apoi") is part of what is said; a symbol like "+" is not
    const joiner = typeof separator === "string" && new RegExp("^\\p{L}+$", "u").test(separator.trim()) ? ` ${separator.trim()} ` : " ";
    const spoken = list.map(spokenLabel).join(joiner);
    if (combined) {
      const label = list.join(list.every((k) => k.length === 1) ? "" : " ");
      return { caps: [{ label, ids: ids.every((cap) => cap.length) ? ids.flat() : [] }], spoken, node: null };
    }
    return { caps: list.map((label, i) => ({ label, ids: ids[i] })), spoken, node: null };
  }

  if (typeof children === "string" || typeof children === "number") {
    const label = String(children);
    if (!label.trim()) return { caps: [], spoken: "", node: null };
    const words = spokenLabel(label);
    return { caps: [{ label, ids: keyIdsOf(label, apple) }], spoken: words === label ? "" : words, node: null };
  }
  return { caps: [], spoken: "", node: children === false ? null : (children ?? null) };
}

function KbdRender(kbdProps: KbdProps) {
  const {
    shortcut,
    keys,
    combined = false,
    platform = "auto",
    size = "md",
    variant = "soft",
    tone = "neutral",
    color,
    radius = "md",
    separator,
    hideOnTouch = false,
    listen = false,
    srLabel,
    classNames,
    className,
    style,
    children,
    ...props
  } = useDefaults("kbd", kbdProps);
  const detected = useIsApple();
  const apple = platform === "apple" || (platform !== "other" && detected);
  const scale: KbdSize = Object.hasOwn(SIZES, size) ? size : "md";
  const look: KbdVariant = Object.hasOwn(NEUTRAL, variant) ? variant : "soft";
  const neutral = !color && (tone === "neutral" || !Object.hasOwn(toneClasses, tone));

  const resolved = resolve(shortcut, keys, children, combined, separator, apple);
  const { caps, node } = resolved;
  const spoken = srLabel?.trim() || resolved.spoken;
  const held = useHeldKeys(
    caps.flatMap((cap) => cap.ids),
    listen,
  );
  if (!caps.length && node === null) return null;

  const isHeld = (cap: Cap) => Boolean(held && cap.ids.length && cap.ids.every((id) => held.has(id)));
  const allHeld = caps.length > 0 && caps.every(isHeld);

  const capClass = cn(
    CAP,
    SIZES[scale],
    RADII[radius]?.[scale] ?? RADII.md[scale],
    (neutral ? NEUTRAL : TONED)[look],
    PRESSED[look],
    look === "ghost" && GHOST_CAP,
  );

  const root = {
    "data-size": scale,
    "data-variant": look,
    "data-tone": color ? "custom" : neutral ? "neutral" : tone,
    "data-platform": apple ? "apple" : "other",
    "data-combined": combined || undefined,
    "data-listening": listen || undefined,
    "data-pressed": (listen && allHeld) || undefined,
    style: { ...colorVars(color), ...style },
  };
  // neutral sets the vars too: a held key fills with --tone (black / white)
  const toned = neutral ? toneClasses.neutral : toneClasses[tone];
  const rootText = neutral ? ROOT_TEXT.neutral : ROOT_TEXT.toned;
  const touch = hideOnTouch && "pointer-coarse:hidden";

  const sr = spoken ? (
    <span data-slot="kbd-sr" className="sr-only select-none">
      {spoken}
    </span>
  ) : null;

  // one cap: the root is the <kbd> itself
  if (node !== null || caps.length === 1) {
    return (
      <kbd
        data-slot="kbd"
        {...root}
        className={cn(toned, rootText, capClass, touch, classNames?.key, className)}
        {...props}
      >
        {node !== null ? (
          spoken ? (
            <span aria-hidden className="contents">
              {node}
            </span>
          ) : (
            node
          )
        ) : spoken ? (
          <CapLabel cap={caps[0]} hidden />
        ) : (
          caps[0].label
        )}
        {sr}
      </kbd>
    );
  }

  const hasSeparator = separator != null && separator !== "" && separator !== false;
  return (
    <span
      data-slot="kbd-group"
      {...root}
      className={cn(
        toned,
        rootText,
        "inline-flex items-center whitespace-nowrap",
        look === "ghost" ? "gap-0.5" : GAPS[scale],
        touch,
        className,
      )}
      {...(props as ComponentProps<"span">)}
    >
      {caps.map((cap, i) => (
        <Fragment key={i}>
          {i > 0 && hasSeparator && (
            <span
              data-slot="kbd-separator"
              aria-hidden
              className={cn("font-sans leading-none", SEPARATOR_SIZES[scale], classNames?.separator)}
            >
              {separator}
            </span>
          )}
          <kbd
            data-slot="kbd-key"
            aria-hidden
            data-pressed={(listen && isHeld(cap)) || undefined}
            className={cn(capClass, classNames?.key)}
          >
            {cap.label}
          </kbd>
        </Fragment>
      ))}
      {sr}
    </span>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const Kbd = withRef(KbdRender, "Kbd");
